import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch } from "redux/store";
import { GenerateRfpData, IUserCreationValues, PaginationData, RfpData, RfpResponse } from "types/dashboard";
import {
  decrementRequests,
  incrementRequests,
  setIsLoading,
  setShowToast,
  updateProfileData,
} from "./application";
import { api } from "api/axiosInterceptor";
import { IValues } from "components/dashboard/settings/ProfileDetails";
import { ISecurityValues } from "components/dashboard/settings/Security";
import { convertToCamelCase } from "utils/utils";
import { DashboardStatsData } from "types/application";

interface DashboardState {
  tableData: RfpData[];
  pageData: {
    archive_rfp: number;
    completed_rfp: number;
    open_rfp: number;
    total: number;
  };
  paginationData: PaginationData;
  loading: boolean;
  generateTableData: {data: GenerateRfpData[], pagination: any};
  dashboardStats : DashboardStatsData,
}

const initialState: DashboardState = {
  tableData: [],
  pageData: {
    archive_rfp: 0,
    completed_rfp: 0,
    open_rfp: 0,
    total: 0,
  },
  paginationData: {
    current_page: 1,
    page_length: 10,
    total_pages: 1,
    total: 1,
  },
  loading: false,
  generateTableData : {data : [], pagination: {}},
  dashboardStats: {
    projectCount: {
      active: 0,
      completed: 0,
      rfpGenerated: {
        active: 0,
        completed: 0
      },
      analysis: {
        active: 0,
        completed: 0
      },
      negotiate: {
        active: 0,
        completed: 0
      }
    },
    totalHoursSaved: 0,
    costSaving: 0,
    hoursSaved: {
      rfpAnalyzeHours: 0,
      rfpGenerateHours: 0,
      negotiateHours: 0,
      totalHoursSaved: 0,
    },
    costSavingData: {
      analyzeStrategicValue: 0,
      costSaving: 0,
      negotiateStrategicValue: 0,
      operational: 0,
      totalStrategicValue: 0
    },
    questionCounts: {
      rfpGenerated: 0,
      analysis: 0,
      negotiate: 0
    }
  }
};

const slice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    getRFPData(state, action: PayloadAction<RfpResponse>) {
      const payload = action.payload;

      state.pageData = {
        archive_rfp: payload?.archive_rfp,
        completed_rfp: payload?.completed_rfp,
        open_rfp: payload?.open_rfp,
        total: payload?.total,
      };

      state.tableData = payload?.data?.map((rfp) => ({
        ...rfp,
        budget:
          rfp?.user_timeline?.timeline &&
          rfp.user_timeline.timeline["ENTER BUDGET"]
            ? rfp.user_timeline.timeline["ENTER BUDGET"].user_activities
            : {},
        key: rfp?.rfp_id,
      }));

      state.paginationData = payload?.pagination;
    },
    decrementOpenRfpCount(state) {
      if (state.pageData.open_rfp > 0) {
        state.pageData.open_rfp -= 1;
      }
    },
    setGenerateRfpTableData : (state, action) => {
      state.generateTableData.data = action.payload.data as GenerateRfpData[];
      state.generateTableData.pagination = action.payload.pagination;

      state.pageData = {
        archive_rfp: action.payload?.archiveRfp,
        completed_rfp: action.payload?.completedRfp,
        open_rfp: action.payload?.openRfp,
        total: action.payload?.total,
      };
    },
    setDashoardStats : (state, action) => {
      state.dashboardStats = action.payload;
    }
  },
});

export const { getRFPData, decrementOpenRfpCount, setGenerateRfpTableData, setDashoardStats } = slice.actions;
export default slice.reducer;

type TSTATE = "Active" | "Inactive" | "Completed";

export function getRFPS(
  state: TSTATE,
  page_name: number,
  sortBy?: string,
  sortOrder?: number
) {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = `/user/rfps/`;
      const { data, status } = await api.post<RfpResponse>(path, {
        state,
        page_length: 10,
        page_number: page_name,
        sort_by: sortBy,
        sort: sortOrder,
      });

      if (status === 200 || status === 201) {
        dispatch(getRFPData(data));
      }
      return status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export const deleteRFP = (rfp_id: string) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const path = `/user/rfps/archive/${rfp_id}/`;
      const { status } = await api.post(path, {
        state: "Inactive",
      });
      return status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  };
};

export const updateRFP = (rfp_id: string, name: string) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const data = {
        rfp_name: name.trim(),
      };
      const path = `/user/rfps/update/${rfp_id}/`;
      const { status } = await api.post(path, data);
      return status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  };
};

export const updateProfile = (values : IValues) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const body = {
        first_name: values.firstName,
        last_name: values.lastName,
        login: values.email,
        title: values.title,
      };
      const path = `/user/profile/update/`;
      const { status } = await api.post(path, body);
      if(status === 200) dispatch(updateProfileData(values));
      return status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  };
};

export const updatePassword = (values : ISecurityValues) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const body = {
        current_password: values.currentPassword,
        new_password: values.newPassword,
      };
      const path = `/user/profile/update/password/`;
      const { status } = await api.post(path, body);
      return status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  };
};

export function getGenerateRFPS(state: "Completed" | "Active", page = 1) {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = `/user/rfps/flow2/data/`;
      const { data, status } = await api.post(path, {
        page_number: page,
        page_length: 10,
        state,
      });
      const formattedData = convertToCamelCase(data);
      if (status === 200 || status === 201) {
        dispatch(setGenerateRfpTableData(formattedData));
      }
      return status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export const addUser = (values : IUserCreationValues) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const body = {
        login: values.email,
        first_name: values.firstName,
        last_name: values.lastName,
        password: values.password,
      };
      const path = `/users/create/`;
      const { status } = await api.post(path, body);
      return status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  };
};

export const updateCompanyLogo = (formData : FormData) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const path = `/upload-user/assests/`;
      const { status } = await api.post(path, formData);
      return status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  };
};

export const getDashboardStats = (days: string) => {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = `/users/dashboard/statistics/?last_days=${days}`;
      const res = await api.get(path);
      const formattedRes = convertToCamelCase(res.data);
      dispatch(setDashoardStats(formattedRes));
      return res;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "There occured some issue while getting the statistics.",
        })
      );
    } finally {
      dispatch(decrementRequests());
    }
  };
};